import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import {
  formatPhoneInput,
  isPlausiblePhone,
  normalizePhone,
  PHONE_ERROR_MESSAGE,
} from "@/lib/phone";

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  relationship_to_community: string;
  referred_by: string;
  time_commitment: string;
}

const INITIAL: FormState = {
  full_name: "",
  email: "",
  phone: "",
  location: "",
  relationship_to_community: "",
  referred_by: "",
  time_commitment: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function MatchmakerSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.full_name.trim()) {
      setError("Please enter your full name.");
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!form.phone.trim()) {
      setError("Please enter a phone number.");
      return;
    }
    if (!isPlausiblePhone(form.phone)) {
      setError(PHONE_ERROR_MESSAGE);
      return;
    }
    if (!form.relationship_to_community.trim()) {
      setError(
        "Please tell us how you know our community and why you want to be a matchmaker.",
      );
      return;
    }
    if (!form.time_commitment) {
      setError("Please select how much time you can commit per week.");
      return;
    }
    if (!consent) {
      setError(
        "Please consent to MAS storing your application before submitting.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "submit-matchmaker-signup",
        {
          body: {
            full_name: form.full_name.trim(),
            email: form.email.trim().toLowerCase(),
            phone: normalizePhone(form.phone),
            location: form.location.trim() || null,
            relationship_to_community: form.relationship_to_community.trim(),
            referred_by: form.referred_by.trim() || null,
            time_commitment: form.time_commitment,
            consent: true,
          },
        },
      );

      if (fnError) throw fnError;
      if (data && data.ok === false) {
        throw new Error(data.error || "Failed to submit application");
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error("matchmaker_signup.submit_failed");
      const msg = err?.message || "Failed to submit application.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="container mx-auto flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-lg text-center">
            <CardHeader>
              <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle2 className="h-10 w-10 text-primary" />
              </div>
              <CardTitle className="text-2xl">Application received</CardTitle>
              <CardDescription className="text-base">
                Thanks — we'll review your application and email you when it's
                approved.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/")} className="w-full sm:w-auto">
                Back to home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="container mx-auto max-w-2xl px-4 py-12 flex-1">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-foreground">
            Become a Matchmaker
          </h1>
          <p className="text-muted-foreground">
            Matchmakers on MAS Matrimonial help review profiles, suggest mutual
            matches between members, and shepherd brothers and sisters toward
            halal marriage. We onboard known, trusted community members on a
            referral basis.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Apply to join the matchmaker team</CardTitle>
            <CardDescription>
              Tell us a bit about yourself. An admin will review and reach out by
              email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="full_name">
                  Full name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="full_name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">
                  Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) =>
                      update("phone", formatPhoneInput(e.target.value))
                    }
                    autoComplete="tel"
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location (optional)</Label>
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                    placeholder="City, State"
                    autoComplete="address-level2"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="relationship_to_community">
                  How do you know our community, and why do you want to be a
                  matchmaker? <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="relationship_to_community"
                  value={form.relationship_to_community}
                  onChange={(e) =>
                    update("relationship_to_community", e.target.value)
                  }
                  rows={5}
                  required
                  placeholder="Tell us about your involvement with MAS or your local masjid, and what draws you to this work."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="referred_by">
                  Referred by (optional)
                </Label>
                <Input
                  id="referred_by"
                  value={form.referred_by}
                  onChange={(e) => update("referred_by", e.target.value)}
                  placeholder="Name of someone who can vouch for you"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time_commitment">
                  How many hours per week can you commit? <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={form.time_commitment}
                  onValueChange={(v) => update("time_commitment", v)}
                >
                  <SelectTrigger id="time_commitment">
                    <SelectValue placeholder="Select an estimate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-2 hrs/week">1–2 hours per week</SelectItem>
                    <SelectItem value="3-5 hrs/week">3–5 hours per week</SelectItem>
                    <SelectItem value="5+ hrs/week">5+ hours per week</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-start space-x-2 rounded-md border border-border bg-muted/30 p-4">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={(checked) => setConsent(checked === true)}
                />
                <Label
                  htmlFor="consent"
                  className="font-normal cursor-pointer leading-snug"
                >
                  I consent to MAS storing my information for the purpose of
                  evaluating my matchmaker application.
                </Label>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full sm:w-auto"
                disabled={submitting}
              >
                {submitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {submitting ? "Submitting..." : "Submit application"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
